import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  createEditor,
  BaseEditor,
  Transforms,
  Editor,
  Descendant,
  Range,
  Point,
  Element,
} from "slate";
import { Slate, Editable, withReact, ReactEditor } from "slate-react";
import { HistoryEditor } from "slate-history";
import moment from "moment";
import { CustomText } from "../Slate/CustomText";
import { MentionElementView } from "./components/MentionElementView";
import { createMentionNode, createParagraphNode } from "../../utils/nodeUtils";
import {
  HeadingOneNode,
  HeadingTwoNode,
  MentionNode,
  ParagraphNode,
} from "../Slate/BaseNode";
import Overlay from "./components/Overlay";
import { OverlayOption } from "./types/OverlayOption";
import SelectParticipantDialog from "../Dialogs/SelectParticipantDialog/SelectParticipantDialog";
import SelectEventDialog from "../SelectEventDialog/SelectEventDialog";
import SelectFileDialog from "../SelectFileDialog/SelectFileDialog";
import { useParticipant } from "../../contexts/ParticipantContext";
import clsx from "clsx";

declare module "slate" {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor & HistoryEditor;
    Element: ParagraphNode | MentionNode | HeadingOneNode | HeadingTwoNode;
    Text: CustomText;
  }
}

const SlateElement = ({ attributes, children, element }: any) => {
  switch (element.type) {
    case "mention":
      return <MentionElementView element={element} attributes={attributes} />;
    case "paragraph":
    default:
      return <p {...attributes}>{children}</p>;
  }
};

const withMentions = (editor: Editor) => {
  const { isInline, isVoid } = editor;

  editor.isInline = (element) =>
    element.type === "mention" ? true : isInline(element);

  editor.isVoid = (element) =>
    element.type === "mention" ? true : isVoid(element);

  return editor;
};

interface SlateTextAreaProps {
  className?: string;
  id: string;
  supportedTypes?: string[];
  supportedSlashOptions?: OverlayOption["option"][];
  placeholder?: string;
  readOnly?: boolean;
  initialValue?: Descendant[];
  onChange?: (value: Descendant[]) => void;
}

export interface SlateEditorRef {
  resetEditor: () => void;
}

const SlateEditor = forwardRef<SlateEditorRef, SlateTextAreaProps>(
  (
    {
      id,
      supportedTypes = ["mention", "slash"],
      supportedSlashOptions = ["event", "participant", "file"],
      placeholder = "Type @ for user mention, / for commands, or # for file mention...",
      readOnly = false,
      initialValue = [createParagraphNode()],
      onChange,
    },
    ref,
  ) => {
    const {participants} = useParticipant();
    const editor = useMemo(() => withMentions(withReact(createEditor())), []);
    const [value, setValue] = useState<Descendant[]>(initialValue);
    const editorRef = useRef<HTMLDivElement | null>(null);

    const [overlay, setOverlay] = useState<{
      type: "user" | "slash" | "file" | null;
      position: { top: number; left: number; width?: number } | null;
      query: string;
      isOpen: boolean;
    }>({
      type: null,
      position: null,
      query: "",
      isOpen: false,
    });

    const [showMentionDialog, setShowMentionDialog] = useState<
      "event" | "participant" | "file" | null
    >(null);

    const openOverlay = useCallback(
      (type: "user" | "slash" | "file", position: any) => {
        setOverlay({ type, position, query: "", isOpen: true });
      },
      [],
    );

    const closeOverlay = useCallback(() => {
      setOverlay({ type: null, position: null, query: "", isOpen: false });
    }, []);

    const setQuery = (query: string) => {
      setOverlay((prev) => ({ ...prev, query }));
    };

    const insertMention = (
      id: string,
      character: string,
      mentionType: "user" | "file" | "event" | "participant",
    ) => {
      const mention = createMentionNode(id, character, mentionType);
      const { selection } = editor;

      if (selection && Range.isCollapsed(selection)) {
        const pointBefore = Editor.before(editor, selection.focus, {
          unit: "character",
        });

        if (pointBefore) {
          const charBefore = Editor.string(editor, {
            anchor: pointBefore,
            focus: selection.focus,
          });

          if (["@", "#", "/"].includes(charBefore)) {
            Transforms.delete(editor, {
              at: { anchor: pointBefore, focus: selection.focus },
            });
          }
        }

        Transforms.insertNodes(editor, mention);

        const pointAfter = Editor.after(
          editor,
          editor.selection!.focus,
        ) as Point;
        if (pointAfter) {
          Transforms.select(editor, pointAfter);
        }

        Transforms.insertText(editor, " ");
        ReactEditor.focus(editor);
      }

      closeOverlay();
    };

    const onSelect = (option: OverlayOption) => {
      if (!editor) return;

      switch (option.type) {
        case "user":
          insertMention(option.id, option.name, "user");
          break;
        case "option":
          if (["event", "participant", "file"].includes(option.id!)) {
            setShowMentionDialog(option.id as "event" | "participant" | "file");
          } else {
            insertMention(option.id, option.name, option.option as any);
          }
          break;
        default:
          closeOverlay();
      }
    };

    const TRIGGER_KEYS: Record<string, "user" | "file" | "slash"> = {
      "@": "user",
      "#": "file",
      "/": "slash",
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      const key = event.key;

      // When overlay is already open
      if (overlay.type) {
        if (key === "Escape") {
          event.preventDefault();
          closeOverlay();
          return;
        }

        if (key === "Backspace") {
          event.preventDefault();
          const updatedQuery = overlay.query.slice(0, -1);
          if (updatedQuery === "" || /\s/.test(updatedQuery)) {
            closeOverlay();
          } else {
            setQuery(updatedQuery);
          }
          return;
        }

        if (
          key.length === 1 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey
        ) {
          event.preventDefault();

          // Stop query if the next character makes it invalid (like space or punctuation)
          const nextQuery = overlay.query + key;
          if (/^\s|\s$/.test(nextQuery)) {
            closeOverlay();
            return;
          }

          setQuery(nextQuery);
          return;
        }

        return; // Do nothing for other keys while overlay is open
      }

      // Trigger overlay if typing trigger character
      if (Object.keys(TRIGGER_KEYS).includes(key)) {
        requestAnimationFrame(() => {
          const { selection } = editor;
          if (!selection || !Range.isCollapsed(selection)) return;

          const domRange = ReactEditor.toDOMRange(editor, selection);
          const rect = domRange.getBoundingClientRect();
          const editorRect = editorRef.current?.getBoundingClientRect();
          if (!editorRect) return;

          const triggerType = TRIGGER_KEYS[key];

          openOverlay(triggerType, {
            top: rect.bottom - editorRect.top + 4,
            left: rect.left - editorRect.left,
            width: editorRect.width,
          });
        });
      }
    };

    const handleReset = () => {
      Transforms.delete(editor, {
        at: {
          anchor: Editor.start(editor, []),
          focus: Editor.end(editor, []),
        },
      });
    };

    useImperativeHandle(ref, () => ({
      resetEditor: handleReset,
    }));

    useEffect(() => {
      if (onChange) onChange(value);
    }, [value, onChange]);

    return (
      <div
        ref={editorRef}
        className="relative"
        style={{ pointerEvents: readOnly ? "none" : "auto" }}
      >
        <Slate editor={editor} initialValue={value} onChange={setValue}>
          <div
  className={clsx(
    "rounded-md",
    readOnly ? "" : "min-h-[96px] border shadow-sm px-3 py-2" // set desired min height here (e.g., same as Input)
  )}
>
  <Editable
    className="w-full p-0 text-sm text-gray-900 placeholder-gray-400 border-none resize-none focus:outline-none focus:ring-0"
    renderElement={(props) => <SlateElement {...props} />}
    placeholder={placeholder}
    onKeyDown={handleKeyDown}
    readOnly={readOnly}
  />
</div>
          {overlay.isOpen && overlay.type && overlay.position && (
            <div
              style={{
                position: "absolute",
                top: overlay.position.top,
                left: overlay.position.left,
                zIndex: 1000,
              }}
            >
              <Overlay
                type={overlay.type!}
                query={overlay.query}
                onSelect={onSelect}
                supportedSlashOptions={supportedSlashOptions}
              />
            </div>
          )}
        </Slate>

        <SelectEventDialog
          isOpen={showMentionDialog === "event"}
          onClose={() => setShowMentionDialog(null)}
          onEventSelected={(event) => {
            insertMention(
              event._id,
              moment(event.eventDate).format("L"),
              "event",
            );
            setShowMentionDialog(null);
          }}
        />
        <SelectParticipantDialog
          participants={participants}
          isOpen={showMentionDialog === "participant"}
          onClose={() => setShowMentionDialog(null)}
          onParticipantSelected={(p) => {
            insertMention(p._id, p.name, "participant");
            setShowMentionDialog(null);
          }}
        />
        <SelectFileDialog
          isOpen={showMentionDialog === "file"}
          onClose={() => setShowMentionDialog(null)}
          onFileSelected={(file) => {
            insertMention(file._id, file.name, "file");
            setShowMentionDialog(null);
          }}
        />
      </div>
    );
  },
);

export default SlateEditor;
